import styled, {css} from 'styled-components';
import {t} from 'i18next';
import SearchIcon from '../../assets/images/icon-search.svg';
import {ChangeEventHandler, FormEvent, useCallback, useState} from 'react';

type PlaysSearchFormProps = {
  formData: HRExperiencesFormData;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onSubmit: (t: string) => void;
};

const PlaysSearchForm = ({
  formData,
  onSubmit,
  onChange,
}: PlaysSearchFormProps) => {
  const [text, setText] = useState('');

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      setText('');
      onSubmit(text);
    },
    [onSubmit, text]
  );

  return (
    <Form onSubmit={handleSubmit}>
      <Inner>
        <SearchBar>
          <IconSearch>Search</IconSearch>
          <Input
            id="search"
            type="text"
            name="fullName"
            value={text}
            placeholder={t('dashboard.search-by-name')}
            autoComplete="off"
            onChange={(e) => setText(e.target.value)}
          />
        </SearchBar>
        <Checkboxes>
          <CheckboxField
            name="playing"
            label={t('dashboard.status.playing')}
            checked={formData['playing']}
            onChange={onChange}
            toolTipText={t('dashboard.tooltip.playing')}
          />
          <CheckboxField
            name="on-boarding"
            label={t('dashboard.status.on-boarding')}
            checked={formData['on-boarding']}
            onChange={onChange}
            toolTipText={t('dashboard.tooltip.on-boarding')}
          />
          <CheckboxField
            name="completed"
            label={t('dashboard.status.completed')}
            checked={formData['completed']}
            onChange={onChange}
            toolTipText={t('dashboard.tooltip.completed')}
          />
        </Checkboxes>
      </Inner>
    </Form>
  );
};

export default PlaysSearchForm;

type CheckboxFieldProps = {
  name: string;
  label: string;
  checked: boolean;
  toolTipText: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
};

const CheckboxField = ({
  name,
  label,
  checked,
  onChange,
  toolTipText,
}: CheckboxFieldProps) => {
  const [tootipOpen, setTooltipOpen] = useState(false);

  return (
    <CheckboxFieldStyled
      onMouseEnter={() => setTooltipOpen(true)}
      onMouseLeave={() => setTooltipOpen(false)}
    >
      <input
        type="checkbox"
        name={name}
        checked={checked}
        onChange={onChange}
      />
      <span>{label}</span>
      <Tooltip visible={tootipOpen}>{toolTipText}</Tooltip>
    </CheckboxFieldStyled>
  );
};

const Form = styled.form`
  margin: 24px 0 16px;
  border: solid 1px #e5e5e5;
  border-radius: 10px;
  padding: 5px 24px;
`;

const Inner = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Input = styled.input`
  flex: 1;
  display: block;
  padding: 0 8px;
  font-size: 16px;
  font-weight: 400;
  border: none;
  font-family: inherit;
  outline: none;
  border-radius: 10px;
  line-height: 40px;

  &:hover {
    box-shadow: 0 0 2px #360cc9;
  }

  &::placeholder {
    color: #bdbbc9;
  }
`;

const IconSearch = styled.i`
  display: block;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  text-indent: 100%;
  overflow: hidden;
  white-space: nowrap;
  background-image: url(${SearchIcon});
  background-position: center;
  background-size: 100%;
  background-repeat: no-repeat;
`;

const Checkboxes = styled.div`
  display: flex;
  gap: 12px;
`;

const CheckboxFieldStyled = styled.label`
  position: relative;
  cursor: pointer;

  input {
    display: none;
  }

  span {
    display: flex;
    align-items: center;
    font-weight: 700;
    font-size: 12px;
    white-space: nowrap;
    gap: 4px;
  }

  span::before {
    content: '';
    display: block;
    width: 14px;
    height: 14px;
    border-radius: 4px;
    background-color: ${(p) => p.theme.colors.white};
    border: solid 2px ${(p) => p.theme.colors.gray300};
    cursor: pointer;
  }

  input:checked + span::before {
    background-color: ${(p) => p.theme.colors.orange500};
    border-color: ${(p) => p.theme.colors.orange500};
  }
`;

const Tooltip = styled.div<{visible: boolean}>`
  position: absolute;
  width: 300px;
  bottom: calc(100% + 12px);
  right: 0;
  padding: 10px;
  border-radius: 15px;
  background-color: ${(p) => p.theme.colors.dark900};
  color: ${(p) => p.theme.colors.white};
  opacity: 0;
  transition: all ease-in-out 300ms;
  pointer-events: none;
  text-align: center;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    right: 20px;
    height: 0;
    width: 0;
    border: solid 10px transparent;
    border-top-color: ${(p) => p.theme.colors.dark900};
  }

  ${(p) =>
    p.visible &&
    css`
      opacity: 1;
    `}
`;
